<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { api } from '$lib/api/client';
  import type { Card, UpdateCardRequest } from '$lib/api/types';
  import LinkCard from './LinkCard.svelte';
  import TextCard from './TextCard.svelte';
  import ImageCard from './ImageCard.svelte';
  import VideoCard from './VideoCard.svelte';
  import SectionCard from './SectionCard.svelte';

  export let card: Card;
  export let editable = false;
  export let isDragging = false;
  export let overrideY: number | undefined = undefined;

  const dispatch = createEventDispatcher<{
    updated: Card;
    deleted: string;
    dragstart: DragEvent;
    dragend: void;
    resized: { cardId: string; width: number; height: number };
  }>();

  let hovered = false;
  let updating = false;

  // Grid position styles
  // Internal units: 2x2 = 1x1 visual, 4x4 = 2x2 visual
  // Using explicit x, y positioning for free-form layout
  // Row height (88px) + gap (20px) = 108px per row
  $: yOffset = overrideY !== undefined ? (overrideY - card.y) * 108 : 0;
  $: gridStyles = `
    --card-width: ${card.width};
    --card-height: ${card.height};
    grid-column: ${card.x + 1} / span ${card.width};
    grid-row: ${card.y + 1} / span ${card.height};
    transform: translateY(${yOffset}px);
  `;

  // Card size options
  const sizeOptions = [
    { width: 2, height: 2, label: '1x1' },
    { width: 4, height: 2, label: '2x1' },
    { width: 2, height: 4, label: '1x2' },
    { width: 4, height: 4, label: '2x2' },
    { width: 6, height: 2, label: '3x1' },
    { width: 8, height: 2, label: '4x1' },
  ];

  async function updateCard(data: UpdateCardRequest) {
    if (updating) return;
    updating = true;

    try {
      const updated = await api.cards.update(card.id, data);
      console.log('Card updated from API:', updated);
      dispatch('updated', updated);
    } catch (error) {
      console.error('Failed to update card:', error);
    } finally {
      updating = false;
    }
  }

  async function deleteCard() {
    if (!confirm('このカードを削除しますか？')) return;

    try {
      await api.cards.delete(card.id);
      dispatch('deleted', card.id);
    } catch (error) {
      console.error('Failed to delete card:', error);
    }
  }

  function handleDataChange(event: CustomEvent<Record<string, unknown>>) {
    updateCard({ data: event.detail });
  }

  function handleSizeChange(width: number, height: number) {
    // Dispatch resize event for CardGrid to handle collision detection
    dispatch('resized', { cardId: card.id, width, height });
  }

  function handleToggleSidebar() {
    const showInSidebar = !card.data?.showInSidebar;
    console.log('Toggle sidebar:', { current: card.data?.showInSidebar, new: showInSidebar, data: { ...card.data, showInSidebar } });
    updateCard({ data: { ...card.data, showInSidebar } });
  }

  function handleDragStart(event: DragEvent) {
    if (!editable) return;
    dispatch('dragstart', event);
  }

  function handleDragEnd() {
    dispatch('dragend');
  }
</script>

<div
  class="card-wrapper relative transition-transform duration-500 ease-out {isDragging ? 'opacity-50' : ''} {hovered ? 'z-[100]' : ''} {card.type === 'section' ? 'section-card' : ''}"
  style={gridStyles}
  on:mouseenter={() => hovered = true}
  on:mouseleave={() => hovered = false}
  draggable={editable}
  on:dragstart={handleDragStart}
  on:dragend={handleDragEnd}
  role="group"
>
  <!-- Drag Overlay (on hover) - covers card to enable dragging over iframes -->
  {#if editable && hovered}
    <div
      class="absolute inset-0 z-20 cursor-grab active:cursor-grabbing rounded-3xl"
    />
  {/if}

  <!-- Delete Button (on hover) - higher z-index -->
  {#if editable && hovered}
    <button
      on:click|stopPropagation={deleteCard}
      class="absolute -top-2 -left-2 z-50 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
      title="削除"
    >
      <i class="lni lni-trash-3 text-xs"></i>
    </button>
  {/if}

  <!-- Card Content -->
  {#if card.type === 'section'}
    <!-- Section cards have no border/background -->
    <div class="h-full {editable && hovered ? 'ring-2 ring-blue-500 rounded-3xl' : ''}">
      <SectionCard data={card.data} {editable} on:change={handleDataChange} />
    </div>
  {:else}
    <div
      class="card h-full {editable && hovered ? 'ring-2 ring-blue-500' : ''}"
      role={editable ? 'group' : undefined}
    >
      {#if card.type === 'link'}
        <LinkCard data={card.data} ogp={card.ogp} {editable} {hovered} on:change={handleDataChange} />
      {:else if card.type === 'text'}
        <TextCard data={card.data} {editable} on:change={handleDataChange} />
      {:else if card.type === 'image'}
        <ImageCard data={card.data} {editable} {hovered} on:change={handleDataChange} />
      {:else if card.type === 'video'}
        <VideoCard data={card.data} {editable} {hovered} on:change={handleDataChange} />
      {:else}
        <div class="p-4 text-gray-500">Unknown card type: {card.type}</div>
      {/if}
    </div>
  {/if}

  <!-- Size Controls (on hover) - not for section cards -->
  {#if editable && hovered && card.type !== 'section'}
    <div class="absolute -bottom-10 left-0 right-0 z-50 flex items-center justify-center gap-1 bg-white rounded-lg shadow-lg p-2">
      {#each sizeOptions as { width, height, label }}
        <button
          on:click|stopPropagation={() => handleSizeChange(width, height)}
          class="px-2 py-1 text-xs rounded {card.width === width && card.height === height ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}"
        >
          {label}
        </button>
      {/each}
      <!-- Sidebar toggle for link cards -->
      {#if card.type === 'link'}
        <span class="mx-1 text-gray-300">|</span>
        <button
          on:click|stopPropagation={handleToggleSidebar}
          class="px-2 py-1 text-xs rounded flex items-center gap-1 {card.data?.showInSidebar ? 'bg-purple-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}"
          title="サイドバーに表示"
        >
          <i class="lni lni-arrow-left"></i>
          左
        </button>
      {/if}
    </div>
  {/if}
</div>
